import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DEFAULT_THEME_COLOR } from '@/types';

interface ThemeColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  error?: string;
}

/**
 * Simple Theme Color Picker Component
 */
export const ThemeColorPicker = React.forwardRef<HTMLInputElement, ThemeColorPickerProps>(({
  value,
  onChange,
  error,
}, ref) => {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Theme Color
      </label>
      
      <div className="relative">
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={value || DEFAULT_THEME_COLOR}
            onChange={(e) => onChange(e.target.value)}
            className="w-8 h-8 rounded border border-gray-300 cursor-pointer flex-shrink-0"
            title="Select theme color"
          />
          <input
            ref={ref}
            type="text"
            value={value || DEFAULT_THEME_COLOR}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 h-10 text-base font-mono px-3 border border-gray-300 rounded-md min-w-0 
                     bg-white text-gray-900 placeholder-gray-500
                     focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary 
                     transition-[border-color,box-shadow] duration-150"
            placeholder="#00809D"
          />
        </div>
        
        {/* Error Message - Overlay style */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="absolute top-full left-0 right-0 mt-1 z-10"
            >
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 shadow-apple-sm">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <p className="text-xs text-gray-500">
        Primary color for buttons, section headers, and highlights
      </p>
    </div>
  );
});

ThemeColorPicker.displayName = 'ThemeColorPicker';
